<script>
import { authMethods } from "@/state/helpers";
import { required, minLength, sameAs } from "vuelidate/lib/validators";

export default {
  data() {
    return {
      password: "",
      confirmPassword: "",
      submitted: false,
      error: null,
      isResetError: false,
      resetting: false,
      token: "",
      tokenMissing: false,
    };
  },
  validations: {
    password: { required, minLength: minLength(8) },
    confirmPassword: { required, sameAsPassword: sameAs("password") },
  },
  created() {
    document.body.classList.add("auth-body-bg");
    this.token = this.$route.query.token || "";
    if (!this.token) {
      this.tokenMissing = true;
      this.isResetError = true;
      this.error =
        "This reset link is invalid or incomplete. Please request a new password reset from the login page.";
    }
  },
  methods: {
    ...authMethods,
    responseMessage(data) {
      if (typeof data === "string") {
        return data;
      }
      if (data && typeof data.message === "string") {
        return data.message;
      }
      return "";
    },
    async tryToReset() {
      this.submitted = true;
      this.resetting = true;
      this.isResetError = false;
      this.error = null;
      this.$v.$touch();

      if (this.tokenMissing || !this.token) {
        this.isResetError = true;
        this.error =
          "This reset link is invalid or expired. Please request a new password reset.";
        this.resetting = false;
        return;
      }

      if (this.$v.$invalid) {
        this.apiFormHandler("Please fix the errors below");
        this.resetting = false;
        return;
      }

      const url = `${this.ROUTES.user}/reset-password`;
      const data = {
        token: this.token,
        password: this.password,
      };

      await this.apiPost(url, data, "Reset Password Error")
        .then((res) => {
          const message = this.responseMessage(res.data);
          this.$router.push({ name: "login" }).then(() => {
            this.apiResponseHandler(
              message || "Password updated successfully. You can now log in.",
              "Reset Password Successful"
            );
          });
        })
        .catch((err) => {
          const errData = err.response?.data;
          this.isResetError = true;
          this.error =
            this.responseMessage(errData) ||
            "Unable to reset password. The link may have expired.";
        })
        .finally(() => {
          this.resetting = false;
        });
    },
  },
};
</script>

<template>
  <div>
    <div>
      <div class="container-fluid p-0">
        <div class="row no-gutters">
          <div class="col-lg-4">
            <div
              class="authentication-page-content p-4 d-flex align-items-center min-vh-100"
            >
              <div class="w-100">
                <div class="row justify-content-center">
                  <div class="col-lg-9">
                    <div>
                      <div class="text-center">
                        <div>
                          <a href="/" class="logo">
                            <img
                              :src="
                                require('@/assets/images/irc-logo.png')
                              "
                              height="80"
                              alt="logo"
                            />
                          </a>
                        </div>

                        <h4 class="font-size-18 mt-5">Set new password</h4>
                        <p class="text-muted">
                          Choose a new password for your IHUMANE account
                        </p>
                      </div>

                      <div class="p-2 mt-5">
                        <b-alert
                          v-model="isResetError"
                          class="mb-4"
                          variant="danger"
                          dismissible
                          >{{ error }}</b-alert
                        >

                        <form
                          v-if="!tokenMissing"
                          class="form-horizontal"
                          @submit.prevent="tryToReset"
                        >
                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-line auti-custom-input-icon"></i>
                            <label for="password">New password</label>
                            <input
                              v-model="password"
                              type="password"
                              class="form-control"
                              id="password"
                              placeholder="Enter new password"
                              :class="{
                                'is-invalid':
                                  submitted && $v.password.$error,
                              }"
                              style="border-radius: 10px"
                            />
                            <div
                              v-if="submitted && $v.password.$error"
                              class="invalid-feedback"
                            >
                              <span v-if="!$v.password.required"
                                >Password is required.</span
                              >
                              <span v-if="!$v.password.minLength"
                                >Password must be at least 8 characters.</span
                              >
                            </div>
                          </div>

                          <div class="form-group auth-form-group-custom mb-4">
                            <i class="ri-lock-line auti-custom-input-icon"></i>
                            <label for="confirmPassword">Confirm password</label>
                            <input
                              v-model="confirmPassword"
                              type="password"
                              class="form-control"
                              id="confirmPassword"
                              placeholder="Confirm new password"
                              :class="{
                                'is-invalid':
                                  submitted && $v.confirmPassword.$error,
                              }"
                              style="border-radius: 10px"
                            />
                            <div
                              v-if="submitted && $v.confirmPassword.$error"
                              class="invalid-feedback"
                            >
                              <span v-if="!$v.confirmPassword.required"
                                >Please confirm your password.</span
                              >
                              <span v-if="!$v.confirmPassword.sameAsPassword"
                                >Passwords do not match.</span
                              >
                            </div>
                          </div>

                          <div class="mt-4 text-center">
                            <button
                              v-if="resetting"
                              class="btn btn-success w-md waves-effect waves-light"
                              type="submit"
                              disabled
                            >
                              Updating...
                            </button>
                            <button
                              v-else
                              class="btn btn-success w-md waves-effect waves-light"
                              type="submit"
                            >
                              Update password
                            </button>
                          </div>
                        </form>
                      </div>

                      <div class="mt-5 text-center">
                        <p>
                          <router-link
                            tag="a"
                            to="/login"
                            class="font-weight-medium text-primary"
                            >Back to login</router-link
                          >
                        </p>
                        <p class="mt-3">
                          © {{ new Date().getFullYear() }} IHUMANE
                        </p>
                        <p>
                          Powered by
                          <a href="https://telecom.connexxiongroup.com">
                            Connexxion Telecom
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-8">
            <div class="authentication-bg">
              <div class="bg-overlay"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
